import { toRaw } from 'vue';

/**
 * Convert design data to the plain data shape accepted by the package
 * format. This is deliberately not a JSON clone: JSON cloning would silently
 * discard values such as undefined/function properties and would hide the
 * place where a runtime object entered the design model.
 *
 * Vue proxies are unwrapped recursively. Only arrays and plain objects are
 * storage data; Konva nodes, DOM objects, Maps/Sets and other runtime values
 * are rejected instead of being copied into a design package.
 */
export function serializeStorageValue(value, path = '$', seen = new WeakMap()) {
    const raw = toRaw(value);

    if (raw === null || typeof raw === 'string' || typeof raw === 'boolean' || typeof raw === 'number') {
        return raw;
    }
    if (raw === undefined) return undefined;
    if (typeof raw === 'bigint' || typeof raw === 'symbol' || typeof raw === 'function') {
        throw new TypeError(`Design data at ${path} is not storage-serializable (${typeof raw})`);
    }

    if (raw instanceof Date) return new Date(raw.getTime());
    if (typeof raw !== 'object') {
        throw new TypeError(`Design data at ${path} is not storage-serializable`);
    }

    const previousPath = seen.get(raw);
    if (previousPath) throw new TypeError(`Design data at ${path} contains a cycle to ${previousPath}`);

    if (Array.isArray(raw)) {
        seen.set(raw, path);
        const result = raw.map((item, index) => serializeStorageValue(item, `${path}[${index}]`, seen));
        seen.delete(raw);
        return result;
    }

    const prototype = Object.getPrototypeOf(raw);
    if (prototype !== Object.prototype && prototype !== null) {
        throw new TypeError(`Design data at ${path} contains a runtime object (${prototype?.constructor?.name ?? 'object'})`);
    }

    seen.set(raw, path);
    const result = {};
    for (const [key, item] of Object.entries(raw)) {
        result[key] = serializeStorageValue(item, `${path}.${key}`, seen);
    }
    seen.delete(raw);
    return result;
}
